"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.facilityZoneCreatedSchema = exports.facilityZoneChangeSchema = exports.facilityZonePreviewInputSchema = exports.facilityZoneChangeInputSchema = exports.facilityZoneProfileSchema = exports.zoneDriftSchema = exports.facilityZoneHistorySchema = exports.ianaTimeZoneSchema = void 0;
/**
 * `RPM-028` — a building's operative time zone, read and changed on the facility-profile
 * administration form (`J-276`).
 *
 * THE ZONE IS CHANGED BY WRITING A NEW VERSION, NEVER BY EDITING ONE. `rpm_facility_profiles` is
 * bitemporal (`03-RPM-SCHEMA.md` §4.2.7): the current row is closed and its successor opened in one
 * transaction, so an alert pinned to the old row keeps the zone it was worked in.
 *
 * THE MIRROR'S ZONE IS SHOWN AND NEVER USED. `mirrorTimeZoneObserved` is "an observation, never an
 * authority"; the form puts it beside the operative zone with its drift state, and nothing on the
 * server reads it to decide a time.
 */
const zod_1 = require("zod");
const rpm_blast_radius_js_1 = require("./rpm-blast-radius.js");
/** The mirror's identifier width. `carease.facilities.id` is varchar(20), and no FK reaches it. */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/**
 * The forms `fn_require_iana_zone` refuses even though `pg_timezone_names` lists them: fixed
 * offsets in `Region/City` clothing and the two duplicate trees (`0013_rpm_validation_triggers.sql`).
 */
const REFUSED_ZONE_PREFIXES = ["Etc/", "posix/", "right/"];
/** True when this runtime's tz database can render the name — `Intl` throws on one it does not know. */
function runtimeKnowsZone(name) {
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: name });
        return true;
    }
    catch {
        return false;
    }
}
/**
 * An IANA `Region/City` zone name: the first of `RPM-028`'s two defences. The second is the
 * database trigger, which asks `pg_timezone_names` the same question; row 2 asks both to refuse.
 *
 * A name with no `/` is an abbreviation or `UTC`, and a facility configured with one stops observing
 * daylight saving, which is why the trigger refuses those too.
 */
exports.ianaTimeZoneSchema = zod_1.z
    .string()
    .trim()
    .min(3)
    .max(64)
    .refine((name) => /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+)+$/.test(name), {
    message: "A time zone is an IANA Region/City name such as America/Chicago.",
})
    .refine((name) => !REFUSED_ZONE_PREFIXES.some((p) => name.startsWith(p)), {
    message: "Fixed-offset zones never observe daylight saving and are refused.",
})
    .refine(runtimeKnowsZone, { message: "This is not a time zone the tz database knows." });
/**
 * What changing this building's zone reaches (`RPM-028` row 3, `FACILITY_ZONE_CHANGED`).
 *
 * The history is the building's recorded readings in this tenant: `from` and `to` are the first and
 * last time one was entered. `affectedAlertCount` is the workups raised for the building. Both are
 * computed when asked and again inside the change, and the change records the second.
 */
exports.facilityZoneHistorySchema = zod_1.z
    .object({
    affectedAlertCount: zod_1.z.number().int().nonnegative(),
    /** The first reading's entry instant, ISO 8601. Null when the building has no history. */
    from: zod_1.z.string().nullable(),
    /** The latest reading's entry instant, ISO 8601. Null when the building has no history. */
    to: zod_1.z.string().nullable(),
})
    .meta({
    id: "FacilityZoneHistory",
    description: "The alerts and the span a zone change on this building reaches",
});
/**
 * Mirror zone drift, computed when it is read (`RPM-061` row 4, `sx-zone-drift`, `J-295`).
 *
 * RAISED, NEVER ACTED ON. The mirror's zone differs from the operative one; the item states the
 * blast radius a correction would have and changes nothing, because ChartVN cannot write the mirror
 * and a value it cannot audit must not silently reinterpret history (`CR-11`). Computed at read
 * because no scheduler exists to run the workflow's scheduled comparison.
 */
exports.zoneDriftSchema = zod_1.z
    .object({
    /** What `carease.facilities.timezone` says now. */
    mirrorZone: zod_1.z.string(),
    /** The zone readings are read in: the profile's. */
    operativeZone: zod_1.z.string(),
    /** Distinct residents with a reading at the building in the last 30 days (`RPM-060`). */
    residents: zod_1.z.number().int().nonnegative(),
    /** Alerts at the building still being worked (`RPM-060`'s in-flight count). */
    alertsInFlight: zod_1.z.number().int().nonnegative(),
    /** The instant the mirror was read and the counts computed, ISO 8601. */
    checkedAt: zod_1.z.string(),
})
    .meta({ id: "ZoneDrift", description: "The mirror's zone differing from the operative one" });
/** One building's current profile, as the form reads it. */
exports.facilityZoneProfileSchema = zod_1.z
    .object({
    facilityId: MIRROR_ID,
    rpmFacilityProfileId: zod_1.z.uuid(),
    /** The operative zone: the only zone a reading's local time is read in (`CR-11`). */
    ianaTimeZone: zod_1.z.string(),
    /** When this version began to hold, ISO 8601. */
    validFrom: zod_1.z.string(),
    /** What `carease.facilities.timezone` said when this version was written. Never an authority. */
    mirrorTimeZoneObserved: zod_1.z.string().nullable(),
    mirrorTimeZoneCheckedAt: zod_1.z.string().nullable(),
    /** True when the mirror's observation differs from the operative zone, or the mirror had none. */
    mirrorTimeZoneDrift: zod_1.z.boolean(),
    /** What the mirror says NOW, read with the profile: the field the form shows (`J-295`). */
    mirrorTimeZoneNow: zod_1.z.string().nullable(),
    /** Drift computed at this read, with its blast radius; null when the mirror agrees or is silent. */
    driftNow: exports.zoneDriftSchema.nullable(),
    history: exports.facilityZoneHistorySchema,
    /** True when the building has history, so a change must state why (`RPM-028`'s MUST). */
    reasonRequired: zod_1.z.boolean(),
})
    .meta({
    id: "FacilityZoneProfile",
    description: "A building's operative time zone and its drift",
});
/**
 * The body of `PUT /api/rpm/v1/admin/facilities/:facilityId/profile`.
 *
 * STRICT, so a body carrying a field this route does not write is refused rather than silently
 * dropped. `statedReason` is optional here and required by the service when the building has
 * history, because only the server knows the history at the moment of the write.
 */
exports.facilityZoneChangeInputSchema = zod_1.z
    .strictObject({
    ianaTimeZone: exports.ianaTimeZoneSchema,
    statedReason: zod_1.z.string().trim().min(1).max(500).optional(),
    /**
     * The blast radius the administrator was shown before confirming (`RPM-060`, `J-294`), carried
     * back so the change records what was seen, not what is true at the write. Required: a zone
     * change alters clinical behaviour, and a commit whose radius was never shown is refused.
     */
    blastRadiusShown: rpm_blast_radius_js_1.blastRadiusShownSchema,
})
    .meta({ description: "A new operative zone, why, and the blast radius shown before it" });
/**
 * The body of `POST /api/rpm/v1/admin/facilities/:facilityId/profile/preview` (`RPM-060`): the zone
 * the change would set, validated exactly as the change validates it.
 */
exports.facilityZonePreviewInputSchema = zod_1.z
    .strictObject({
    ianaTimeZone: exports.ianaTimeZoneSchema,
})
    .meta({ description: "The operative zone a change would set" });
/** What the change wrote: the new current version, the one it closed, and what it reached. */
exports.facilityZoneChangeSchema = zod_1.z
    .object({
    /** False when the zone asked for is the one in force: nothing is written. */
    changed: zod_1.z.boolean(),
    profile: exports.facilityZoneProfileSchema,
    /** The version this change closed. Null when nothing changed. */
    previousProfileId: zod_1.z.uuid().nullable(),
    /** The count and span as recorded with the change, computed inside its transaction. */
    recorded: exports.facilityZoneHistorySchema.nullable(),
})
    .meta({ id: "FacilityZoneChange", description: "The zone version a change wrote" });
/**
 * What `POST /api/rpm/v1/admin/facilities/:facilityId/profile` wrote: a building's FIRST profile, the
 * one that gives it a zone (`RPM-061`, `J-295`). Its body is the change's (`facilityZoneChangeInputSchema`):
 * the zone, a reason where the building has history, and the blast radius shown before the confirm.
 */
exports.facilityZoneCreatedSchema = zod_1.z
    .object({
    profile: exports.facilityZoneProfileSchema,
    /** The count and span as recorded with the write, computed inside its transaction. */
    recorded: exports.facilityZoneHistorySchema,
})
    .meta({ id: "FacilityZoneCreated", description: "A building's first profile, with its zone" });
//# sourceMappingURL=rpm-facility-profile.js.map